/**
 * useTagInventory / useTagInventoryThreshold / useVeraProductsByTag / useBulkAssignDefaultTag
 *
 * Hooks for the default-tag inventory endpoints:
 *   GET /shop/tags/products/:tagShopProductId/vera-products  → Vera products with this default tag
 *   POST /shop/tags/products/:tagShopProductId/assign        → bulk-assign default tag
 *   GET /shop/tags/products/:tagShopProductId/threshold      → demand + possession + capacity
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiCall, api } from 'services'

// ─── Types ────────────────────────────────────────────────────────────────────

export type VeraProductForTag = {
  id: string
  title: string
  sku: string
  /** Product type / category (e.g. Watches, Jewellery). */
  type: string | null
  images: string[]
  available: boolean
  quantity: number
  /** Units already tagged (certified) = min(quantity, certificate count). */
  tagged: number
  /** Units still needing a tag = max(0, quantity − certificate count). Drives tag demand. */
  untagged: number
  /** Link to the product on the brand storefront. */
  product_url: string | null
  /** Where the product synced from (e.g. shopify); null when manually added. */
  source: string | null
  default_tag_id: string | null
}

export type TagInventoryThreshold = {
  demand: number
  /**
   * VERA-455 — the three possession stages for this SKU, replacing `supply`/`gap` (which
   * came from the never-decremented `company_tag_inventory.ordered_qty`).
   */
  at_vera_qty: number
  incoming_qty: number
  available_qty: number
  /** `available_qty − uncertified_qty`; negative = shortfall. */
  capacity: number
  uncertified_qty: number
  tag_shop_product_id?: string | null
  // VERA-246: persisted per-tag settings for the two settings modals.
  threshold: number
  threshold_type: 'number' | 'percentage'
  send_threshold_email: boolean
  auto_order: boolean
  auto_order_qty?: number | null
  auto_order_payment_method_id?: string | null
}

// ─── Vera products with this tag as default ───────────────────────────────────

type UseVeraProductsByTagParams = {
  tagShopProductId: string
  limit?: number
  offset?: number
}

export function useVeraProductsByTag(
  params: UseVeraProductsByTagParams,
  options?: { enabled?: boolean }
) {
  const { tagShopProductId, limit = 100, offset = 0 } = params
  const enabled = options?.enabled !== false && !!tagShopProductId

  return useQuery({
    queryKey: ['vera-products-by-tag', tagShopProductId, limit, offset],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: `/shop/tags/products/${tagShopProductId}/vera-products`,
        params: { limit, offset },
      })
      return response.data as { products: VeraProductForTag[]; total: number }
    },
    enabled,
    staleTime: 1000 * 30,
  })
}

// ─── Bulk-assign default tag ─────────────────────────────────────────────────

type BulkAssignDefaultTagParams = {
  tagShopProductId: string
  product_ids: string[]
}

type BulkAssignDefaultTagResponse = {
  updated_count: number
  updated_product_ids: string[]
}

type UseBulkAssignDefaultTagOptions = {
  onSuccess?: (data: BulkAssignDefaultTagResponse) => void
  onError?: (error: Error) => void
}

export function useBulkAssignDefaultTag(options?: UseBulkAssignDefaultTagOptions) {
  const queryClient = useQueryClient()

  return useMutation<BulkAssignDefaultTagResponse, Error, BulkAssignDefaultTagParams>({
    mutationFn: async ({ tagShopProductId, product_ids }) => {
      const response = await api.post(
        `/shop/tags/products/${tagShopProductId}/assign`,
        { product_ids },
      )
      return response.data
    },
    onSuccess: (data, variables) => {
      // Invalidate the vera-products-by-tag list so the assign shows immediately
      queryClient.invalidateQueries({ queryKey: ['vera-products-by-tag', variables.tagShopProductId] })
      // Invalidate all products so default_tag_id updates appear
      queryClient.invalidateQueries({ queryKey: ['products'] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
  })
}

// ─── Bulk-unassign default tag ───────────────────────────────────────────────

export function useBulkUnassignDefaultTag(options?: UseBulkAssignDefaultTagOptions) {
  const queryClient = useQueryClient()

  return useMutation<BulkAssignDefaultTagResponse, Error, BulkAssignDefaultTagParams>({
    mutationFn: async ({ tagShopProductId, product_ids }) => {
      const response = await api.post(
        `/shop/tags/products/${tagShopProductId}/unassign`,
        { product_ids },
      )
      return response.data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vera-products-by-tag', variables.tagShopProductId] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
  })
}

// ─── Inventory threshold ──────────────────────────────────────────────────────

type UseTagInventoryThresholdParams = {
  tagShopProductId: string
}

export function useTagInventoryThreshold(
  params: UseTagInventoryThresholdParams,
  options?: { enabled?: boolean }
) {
  const { tagShopProductId } = params
  const enabled = options?.enabled !== false && !!tagShopProductId

  return useQuery({
    queryKey: ['tag-inventory-threshold', tagShopProductId],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: `/shop/tags/products/${tagShopProductId}/threshold`,
      })
      return response.data as TagInventoryThreshold
    },
    enabled,
    staleTime: 1000 * 60, // 1 minute
  })
}
